import React, { useState, useEffect } from 'react';
import Paper from '@mui/material/Paper';
import useApi from '../../hooks/useApi';
import LoadingOverlay from '../../loadingOverlay/LoadingOverlay';
import useSnackbar from '../../hooks/useSnackbar';
import Divider from '@mui/material/Divider';
import Stack from '@mui/material/Stack';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';

import Stepper from './components/Stepper';
import Domain from './components/Domain';
import Instruction from './components/Instruction';

const EmailAuthentication = () => {
    const [isLoading, setIsLoading] = useState(true);
    const [verifyLoading, setVerifyLoading] = useState(false);
    const { handleGet } = useApi();
    const [account, setAccount] = useState(null);
    const [boolStepper, setBoolStepper] = useState([]);
    const { showSnackbar } = useSnackbar();
    const [openDialog, setOpenDialog] = useState(false);
    const [failedKeys, setFailedKeys] = useState([]);

    const handleFetchAccount = async () => {
        const response = await handleGet('account');

        if (!response.ok) {
            setIsLoading(false);
            return;
        }

        const data = await response.json();

        const steps = ['mxCheck', 'spf', 'dkim', 'dmarc'];
        const dateStatusArray = steps.map(field => !!data[field]);

        setBoolStepper(dateStatusArray);

        setAccount(data);
    };

    const handleSubmit = async () => {
        setVerifyLoading(true);

        const response = await handleGet(`account/${account.id}/email-authentication`);

        const data = await response.json();

        let count = 0;

        if (data.mxCheckResult === true) count += 1;
        if (data.spfCheckResult === true) count += 1;
        if (data.dmarcCheckResult === true) count += 1;
        if (data.dkimCheckResult?.success === true) count += 1;

        if (!response.ok) {
            showSnackbar('Checks not completed. Please contact support', 'error');
            setIsLoading(false);
            setVerifyLoading(false);
            return;
        }

        if (data.dkimCheckResult?.success === false && data.dkimCheckResult.failedKeys) {
            setFailedKeys(data.dkimCheckResult.failedKeys);
            setOpenDialog(true);
        }

        if (count === 4) {
            showSnackbar('All checks completed', 'success');
            setVerifyLoading(false);
        }
        else {
            showSnackbar(`${count} out of 4 checks completed`, 'warning');
            setVerifyLoading(false);
        }

        await handleFetchAccount();
    };

    useEffect(() => {
        handleFetchAccount();
    }, []);

    useEffect(() => {
        account && setIsLoading(false);
    }, [account]);

    if (isLoading) {
        return (
            <LoadingOverlay />
        );
    }

    return (
        <Stack spacing={2}>
            <Paper>
                <Stepper data={boolStepper} />
            </Paper>
            <Paper>
                <Domain account={account} onRefresh={handleFetchAccount} />
            </Paper>
            <Paper>
                <Instruction account={account} handleSubmit={handleSubmit} />
                <Divider />
            </Paper>
            {verifyLoading && <LoadingOverlay />}
            <Dialog
                open={openDialog}
                onClose={() => setOpenDialog(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    DKIM Check Failed
                </DialogTitle>
                <DialogContent dividers>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                        The following DKIM keys failed:
                    </Typography>
                    <Stack spacing={1}>
                        {failedKeys?.map((key, index) => (
                            <Alert key={index} severity="warning">{key}</Alert>
                        ))}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenDialog(false)}>Close</Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

export default EmailAuthentication;